import { useMutation } from '@tanstack/react-query'
import { Button, notification, Spin } from 'antd'
import React, { type JSX } from 'react'

import { SettingsCard, SettingsRow, SettingsRows } from '../../../Settings'
import { useBillingClient } from '../../queries'

import { PassThroughFee, patchBillingClient } from 'api/billing'
import { InlineEditField } from 'shared/components/InlineEditField/InlineEditField'

const FEE_LABELS: Record<PassThroughFee['communicationChannel'], string> = {
  sms: 'SMS Carrier Fee',
  mms: 'MMS Carrier Fee',
  whatsapp: 'WhatsApp Fee',
}

const toPassThroughFeesBody = ({
  fees,
  nextValue,
  selectedFee,
}: {
  fees: PassThroughFee[]
  nextValue: string
  selectedFee: PassThroughFee
}) => ({
  passThroughFees: fees.map((fee) => ({
    communicationChannel: fee.communicationChannel,
    value: fee.communicationChannel === selectedFee.communicationChannel ? nextValue : fee.value || null,
  })),
})

const toResetPassThroughFeesBody = (fees: PassThroughFee[]) => ({
  passThroughFees: fees.map((fee) => ({
    communicationChannel: fee.communicationChannel,
    value: null,
  })),
})

export const PassThroughFees = ({ clientId }: { clientId: string }): JSX.Element | null => {
  const { data, reset: refetchBillingClient, isInitialLoading } = useBillingClient(clientId)
  const fees = data?.passThroughFees || []
  const { mutate: resetFees, isLoading: isResettingFees } = useMutation(patchBillingClient, {
    onError() {
      notification.error({
        message: 'Could not reset pass-through fees. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
    onSuccess() {
      notification.success({
        message: 'Pass-through fees reset.',
        placement: 'bottomRight',
        duration: 2,
      })
      refetchBillingClient()
    },
  })

  if (!isInitialLoading && fees.length === 0) {
    return null
  }

  return (
    <SettingsCard
      extra={
        <Button
          disabled={isInitialLoading || fees.length === 0}
          loading={isResettingFees}
          onClick={() => resetFees({ clientId, body: toResetPassThroughFeesBody(fees) })}
        >
          Reset Fees
        </Button>
      }
      title="Estimated Campaign Cost Variables"
    >
      <SettingsRows>
        {isInitialLoading ? (
          <SettingsRow label="Carrier Fees">
            <Spin />
          </SettingsRow>
        ) : (
          fees.map((fee) => {
            const value = fee.value || fee.defaultValue

            return (
              <SettingsRow key={fee.communicationChannel} label={FEE_LABELS[fee.communicationChannel]}>
                <InlineEditField
                  defaultValue={value}
                  key={`${fee.communicationChannel}-${value}`}
                  name={FEE_LABELS[fee.communicationChannel]}
                  onSuccess={refetchBillingClient}
                  pattern={/^\d*\.?\d*$/}
                  placeholder={value}
                  prepare={(nextValue) => ({
                    clientId,
                    body: toPassThroughFeesBody({ fees, nextValue, selectedFee: fee }),
                  })}
                  updateEntity={patchBillingClient}
                />
              </SettingsRow>
            )
          })
        )}
      </SettingsRows>
    </SettingsCard>
  )
}
